import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import apiService from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import StripePaymentForm from './StripePaymentForm';

// Initialize Stripe with the publishable key
const stripePromise = loadStripe(`${process.env.STRIPE_PUBLISHABLE_KEY}`);

const SubscriptionManager = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { 
    subscriptionPlans, 
    userSubscriptions, 
    fetchSubscriptionPlans, 
    fetchUserSubscriptions 
  } = useSubscription();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [processingSubscription, setProcessingSubscription] = useState(false);

  // Fetch subscription plans and user subscriptions on component mount
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchSubscriptionPlans(),
          fetchUserSubscriptions(),
          fetchPaymentMethods()
        ]);
        setError(null);
      } catch (err) {
        console.error('Error initializing subscription data:', err);
        setError(t('subscription.errors.initialization'));
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, [fetchSubscriptionPlans, fetchUserSubscriptions, t]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await apiService.getPaymentMethods();
      setPaymentMethods(response.data || []);
      
      // Set the default payment method if available
      const defaultMethod = response.data?.find(method => method.isDefault);
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod.id);
      } else if (response.data?.length > 0) {
        setSelectedPaymentMethod(response.data[0].id);
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      // Don't set an error here, as it's not critical for viewing plans
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    
    // If user has payment methods, show subscription confirmation
    // Otherwise, show payment form
    if (paymentMethods.length > 0) {
      setShowPaymentForm(false);
    } else {
      setShowPaymentForm(true);
    }
  };

  const handlePaymentMethodAdded = async (response) => {
    // Refresh payment methods
    await fetchPaymentMethods();
    setShowPaymentForm(false);
    
    // If a plan was selected, proceed with subscription
    if (selectedPlan) {
      handleSubscribe();
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    
    setProcessingSubscription(true);
    try {
      await apiService.subscribeToPlan(selectedPlan.id);
      
      addToast({
        type: 'success',
        message: t('subscription.success.subscribed', { plan: selectedPlan.name })
      });
      
      // Refresh user subscriptions
      await fetchUserSubscriptions();
      
      // Reset state
      setSelectedPlan(null);
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || t('subscription.errors.subscribe'));
      addToast({
        type: 'error',
        message: err.message || t('subscription.errors.subscribe')
      });
    } finally {
      setProcessingSubscription(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm(t('subscription.confirmCancel'))) return;
    
    try {
      await apiService.cancelSubscription(subscriptionId);
      
      addToast({
        type: 'success',
        message: t('subscription.success.cancelled')
      });
      
      // Refresh user subscriptions
      await fetchUserSubscriptions();
    } catch (err) {
      console.error('Cancellation error:', err);
      addToast({
        type: 'error',
        message: err.message || t('subscription.errors.cancel')
      });
    }
  };

  const formatPrice = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100);
  };

  const formatCardInfo = (method) => {
    if (!method) return '';
    return `${method.card.brand.toUpperCase()} •••• ${method.card.last4}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* User's Current Subscriptions */}
      <div>
        <h2 className="text-xl font-semibold mb-4">{t('subscription.yourSubscriptions')}</h2>
        
        {userSubscriptions.length === 0 ? (
          <div className="p-6 border border-gray-200 rounded-md bg-gray-50 text-center">
            <p className="text-gray-600">{t('subscription.noActiveSubscriptions')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {userSubscriptions.map((subscription) => (
              <div key={subscription.id} className="p-6 border border-gray-200 rounded-md">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium">{subscription.plan.name}</h3>
                    <p className="text-gray-600">{subscription.plan.description}</p>
                    <div className="mt-2">
                      <span className="font-medium">{t('subscription.status')}:</span> 
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                        subscription.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {subscription.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="mt-1">
                      <span className="font-medium">{t('subscription.period')}:</span> 
                      <span className="ml-2">
                        {new Date(subscription.currentPeriodStart).toLocaleDateString()} - 
                        {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="mt-1">
                      <span className="font-medium">{t('subscription.price')}:</span> 
                      <span className="ml-2">{formatPrice(subscription.plan.price)}</span>
                    </div>
                  </div>
                  
                  {subscription.status === 'active' && (
                    <button
                      onClick={() => handleCancelSubscription(subscription.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                      {t('subscription.cancel')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Subscription Plans */}
      <div>
        <h2 className="text-xl font-semibold mb-4">{t('subscription.availablePlans')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subscriptionPlans.map((plan) => (
            <div 
              key={plan.id} 
              className={`p-6 border rounded-md ${
                selectedPlan?.id === plan.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              } transition-colors cursor-pointer`}
              onClick={() => handleSelectPlan(plan)}
            >
              <h3 className="text-lg font-medium">{plan.name}</h3>
              <div className="text-2xl font-bold my-2">
                {formatPrice(plan.price)}
                <span className="text-sm font-normal text-gray-600">/{plan.interval}</span>
              </div>
              <p className="text-gray-600 mb-4">{plan.description}</p>
              
              <ul className="space-y-2 mb-4">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <button
                className={`w-full py-2 px-4 rounded-md ${
                  selectedPlan?.id === plan.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                } transition-colors`}
              >
                {selectedPlan?.id === plan.id ? t('subscription.selected') : t('subscription.select')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Subscription Confirmation or Payment Form */}
      {selectedPlan && (
        <div className="p-6 border border-gray-200 rounded-md bg-gray-50">
          <h3 className="text-lg font-medium mb-4">
            {t('subscription.confirmSubscription', { plan: selectedPlan.name })}
          </h3>
          
          {showPaymentForm ? (
            <div>
              <p className="mb-4">{t('subscription.addPaymentMethod')}</p>
              <Elements stripe={stripePromise}>
                <StripePaymentForm 
                  onSuccess={handlePaymentMethodAdded}
                  buttonText={t('payment.saveAndSubscribe')}
                  processingText={t('payment.processing')}
                />
              </Elements>
              <button
                onClick={() => {
                  setShowPaymentForm(false);
                  setSelectedPlan(null);
                }}
                className="mt-4 text-gray-600 hover:text-gray-800"
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <div>
              {paymentMethods.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('payment.selectPaymentMethod')}
                  </label>
                  <div className="space-y-2">
                    {paymentMethods.map((method) => (
                      <div 
                        key={method.id}
                        className={`p-3 border rounded-md cursor-pointer ${
                          selectedPaymentMethod === method.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                        onClick={() => setSelectedPaymentMethod(method.id)}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            checked={selectedPaymentMethod === method.id}
                            onChange={() => setSelectedPaymentMethod(method.id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-3">{formatCardInfo(method)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setShowPaymentForm(true)}
                    className="mt-2 text-blue-600 hover:text-blue-800"
                  >
                    {t('payment.useNewCard')}
                  </button>
                </div>
              )}
              
              <div className="flex space-x-4">
                <button
                  onClick={handleSubscribe}
                  disabled={processingSubscription || (paymentMethods.length > 0 && !selectedPaymentMethod)}
                  className={`py-2 px-4 rounded-md ${
                    processingSubscription || (paymentMethods.length > 0 && !selectedPaymentMethod)
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  } transition-colors`}
                >
                  {processingSubscription 
                    ? t('subscription.processing') 
                    : t('subscription.confirmSubscribe', { price: formatPrice(selectedPlan.price) })}
                </button>
                
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="py-2 px-4 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;
